import React, { Fragment, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { isAssetWithPreview } from '@experiment-management-shared/utils/assetsUtils';
import { getUniqValuesFromArrayOfObjects } from '@/helpers/generalHelpers';
import FilterPopover from '@shared/components/FilterPopover';
import AssetPreviewContainer from '@experiment-management-shared/components/AssetsPreview/AssetPreviewContainer';
import { SearchInput } from '@ds';
import { ArtifactVersionAssetsTable } from '@artifacts/components/ArtifactVersionPage/ArtifactVersionAssetsTab/ArtifactVersionAssetsTable';
import { useVersionAssetsPaginationContext } from '@artifacts/contexts';
import { debounce } from 'lodash';

const getDirectoryFilters = items => {
  return [
    {
      groupLabel: 'Asset type',
      filterKey: 'type',
      options: getUniqValuesFromArrayOfObjects(items, 'type').map(type => ({
        label: type,
        value: type
      }))
    }
  ];
};

const DEBOUNCE_DELAY = 500;

const ArtifactVersionAssetsTab = ({ artifactVersion, isFetching }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [localSearchQuery, setLocalSearchQuery] = useState('');

  const {
    searchQuery,
    setSearchQuery,
    setCurrentPage,
    assetTypes,
    setAssetTypes
  } = useVersionAssetsPaginationContext();

  const debouncedSearch = useCallback(
    debounce(query => {
      setCurrentPage(0);
      setSearchQuery(query);
    }, DEBOUNCE_DELAY),
    []
  );

  useEffect(() => {
    if (searchQuery !== localSearchQuery) {
      setLocalSearchQuery(searchQuery);
    }
  }, [searchQuery]);

  const handleSearchChange = query => {
    setLocalSearchQuery(query);
    debouncedSearch(query);
  };

  const handleFilterChange = useCallback(
    (filterKey, values) => {
      const update = {
        ...assetTypes,
        [filterKey]: values
      };
      setAssetTypes(update);
    },
    [assetTypes]
  );

  const handleRowClick = useCallback(file => {
    setSelectedFile(file);
    setIsPreviewOpen(isAssetWithPreview(file, true));
  }, []);

  const renderActionFields = () => {
    // we should update FilterPopover
    // getDirectoryFilters should return a simple array in this case
    // since it's connected to other components in the app i avoided it
    const filters = getDirectoryFilters(artifactVersion.files);
    return (
      <div className="action-fields-container">
        <div className="field-group">
          {filters.map(filter => (
            <Fragment key={filter.filterKey}>
              <FilterPopover
                className="light"
                handleFilterChange={(type, values) => {
                  handleFilterChange(filter.filterKey, values);
                }}
                selectedFilters={get(assetTypes, [filter.filterKey], [])}
                filter={filter}
              />
            </Fragment>
          ))}
        </div>
        <div style={{ width: 225 }}>
          <SearchInput
            placeholder="Search file or directory"
            onChange={e => handleSearchChange(e.target.value)}
            value={localSearchQuery}
            onClearIconClick={() => handleSearchChange('')}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="artifact-version-files-tab">
      <div className="action-fields-container">{renderActionFields()}</div>
      <ArtifactVersionAssetsTable
        rows={artifactVersion.files}
        totalRowCount={artifactVersion.totalAssetsCnt}
        experimentKey={artifactVersion.experimentKey}
        artifactVersionId={artifactVersion.artifactVersionId}
        handleRowClick={handleRowClick}
        isFetching={isFetching}
      />
      {isPreviewOpen && (
        <AssetPreviewContainer
          experimentKey={artifactVersion.experimentKey}
          file={selectedFile}
          closeHandler={() => setIsPreviewOpen(false)}
        />
      )}
    </div>
  );
};

ArtifactVersionAssetsTab.propTypes = {
  artifactVersion: PropTypes.object.isRequired,
  isFetching: PropTypes.bool.isRequired
};

export default ArtifactVersionAssetsTab;
